import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

export const ARTICLE_TYPES = {
  NEWS: 'News',
  INFORMATION_LINK: 'Informational Link',
};

export const getClientArticleSettings = clientDetails => {
  const clientArticleSettings = _get(
    clientDetails,
    'metadata.articlesSettings',
    {},
  );

  const clientGroupArticlesSettings = _get(
    clientDetails,
    'clientGroup.metadata.articlesSettings',
    {},
  );
  const articlesSettings = {
    ...clientGroupArticlesSettings,
    ...clientArticleSettings,
  };

  return {
    alwaysShowInformationalLink: _get(
      articlesSettings,
      'alwaysShowInformationalLink',
      false,
    ),
    articlesMinimumWordCount: _get(
      articlesSettings,
      'articlesMinimumWordCount',
      null,
    ),
  };
};

export const hasClientAccess = ({
  item,
  clientDetails,
  paths = {
    includedClientsPath: 'clientIncludeCollection.items',
    excludedClientsPath: 'clientExcludeCollection.items',
    itemClientShortNamePath: 'shortName',
    itemClientGroupShortNamePath: 'shortName',
  },
}) => {
  if (_isEmpty(item) || _isEmpty(clientDetails)) return false;

  const {
    includedClientsPath,
    excludedClientsPath,
    itemClientShortNamePath,
    itemClientGroupShortNamePath,
  } = paths;

  const clientShortName = _get(clientDetails, 'shortName');
  const clientGroupShortName = _get(clientDetails, 'clientGroup.shortName');

  const itemIncludedClients = _get(item, includedClientsPath);

  if (
    !_isEmpty(itemIncludedClients) &&
    !(itemIncludedClients.length === 1 && itemIncludedClients[0] === 'none')
  ) {
    return itemIncludedClients.some(
      client =>
        (itemClientShortNamePath
          ? _get(client, itemClientShortNamePath)
          : client) === clientShortName ||
        (clientGroupShortName &&
          (itemClientGroupShortNamePath
            ? _get(client, itemClientGroupShortNamePath)
            : client) === clientGroupShortName),
    );
  }

  const itemExcludedClients = _get(item, excludedClientsPath);
  if (!_isEmpty(itemExcludedClients)) {
    return !itemExcludedClients.some(
      client =>
        (itemClientShortNamePath
          ? _get(client, itemClientShortNamePath)
          : client) === clientShortName ||
        (clientGroupShortName &&
          (itemClientGroupShortNamePath
            ? _get(client, itemClientGroupShortNamePath)
            : client) === clientGroupShortName),
    );
  }

  return true;
};

export const filterItemsByClientAccess = ({ items, clientDetails, paths }) => {
  if (!Array.isArray(items) || !clientDetails) return [];

  return items.filter(item => hasClientAccess({ item, clientDetails, paths }));
};

export const hasClientAccessToFeature = ({
  featuresSiteConfig,
  featureName,
  clientDetails,
}) => {
  const featureConfig = _get(featuresSiteConfig, ['config', featureName]);
  if (!_get(featureConfig, 'enabled')) {
    return false;
  }

  // Check client
  const clientsInclude = _get(featureConfig, 'clientsInclude', []);
  const clientsExclude = _get(featureConfig, 'clientsExclude', []);

  const hasClientsInclude = !_isEmpty(clientsInclude);
  const clientShortName = _get(clientDetails, 'shortName');

  const isClientIncluded = clientsInclude.includes(clientShortName);
  const isClientExcluded = clientsExclude.includes(clientShortName);

  if ((hasClientsInclude && !isClientIncluded) || isClientExcluded) {
    return false;
  }

  // Check client group
  const clientGroupShortName = _get(clientDetails, 'clientGroup.shortName');
  const clientGroupsInclude = _get(featureConfig, 'clientGroupsInclude', []);
  const clientGroupsExclude = _get(featureConfig, 'clientGroupsExclude', []);

  const hasClientGroupsInclude = !_isEmpty(clientGroupsInclude);
  const isClientGroupIncluded = clientGroupsInclude.includes(
    clientGroupShortName,
  );
  const isClientGroupExcluded = clientGroupsExclude.includes(
    clientGroupShortName,
  );

  if (
    // We want to give priority to client settings(if there is a client included, we don't want to check the client group settings)
    !isClientIncluded &&
    clientGroupShortName &&
    ((hasClientGroupsInclude && !isClientGroupIncluded) ||
      isClientGroupExcluded)
  ) {
    return false;
  }

  // Check brand sector
  const clientBrandSector = _get(clientDetails, 'sector');
  const brandSectorsInclude = _get(featureConfig, 'brandSectorsInclude', []);
  const brandSectorsExclude = _get(featureConfig, 'brandSectorsExclude', []);

  const hasBrandSectorsInclude = !_isEmpty(brandSectorsInclude);
  const isBrandSectorIncluded = brandSectorsInclude.includes(clientBrandSector);
  const isBrandSectorExcluded = brandSectorsExclude.includes(clientBrandSector);

  if (
    clientBrandSector &&
    ((hasBrandSectorsInclude && !isBrandSectorIncluded) ||
      isBrandSectorExcluded)
  ) {
    return false;
  }

  return true;
};

export const hasClientAccessToPersonalization = ({
  featuresSiteConfig,
  clientDetails,
}) =>
  hasClientAccessToFeature({
    featuresSiteConfig,
    featureName: 'personalizationEnabled',
    clientDetails,
  });
